import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Admin-only: retrieve the FULL chat transcript between any two staff members,
// ignoring per-user "delete" (clear) watermarks. Backed by GET /chat/admin/transcript.
const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const ACCENT = 'var(--caam-accent)';

export default function ChatTranscripts() {
  const [staff, setStaff] = useState([]);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(API + '/staff', { headers: hdrs() })
      .then(r => setStaff((r.data || []).slice().sort((x, y) =>
        (x.first_name + x.last_name).localeCompare(y.first_name + y.last_name))))
      .catch(() => {});
  }, []);

  const load = async () => {
    if (!a || !b || a === b) { setError('Pick two different people.'); return; }
    setLoading(true); setError(''); setMessages(null);
    try {
      const r = await axios.get(`${API}/chat/admin/transcript?a=${a}&b=${b}`, { headers: hdrs() });
      setMessages(r.data || []);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Admins only.' : (e.response?.data?.error || 'Failed to load transcript.'));
    } finally { setLoading(false); }
  };

  const name = (id) => { const s = staff.find(x => String(x.id) === String(id)); return s ? `${s.first_name} ${s.last_name}` : id; };
  const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const selectStyle = { padding: '9px 11px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', minWidth: '200px', background: '#fff' };

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 18px' }}>
        View the complete message history between two staff members. This includes messages either
        person has "deleted" from their own view — nothing is hidden from admins.
      </p>
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>Person A</label>
          <select value={a} onChange={e => setA(e.target.value)} style={selectStyle}>
            <option value="">Select…</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Person B</label>
          <select value={b} onChange={e => setB(e.target.value)} style={selectStyle}>
            <option value="">Select…</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </div>
        <button onClick={load} disabled={loading}
          style={{ padding: '9px 18px', background: ACCENT, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Loading…' : 'Load transcript'}
        </button>
      </div>

      {error && <div style={{ color: '#9B3A1F', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

      {messages && (
        messages.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>No messages between {name(a)} and {name(b)}.</div>
        ) : (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: '#FAFAFA', borderBottom: '1px solid #E5E7EB', fontSize: '12px', color: '#6B7280' }}>
              {messages.length} message{messages.length === 1 ? '' : 's'} · {name(a)} ↔ {name(b)}
            </div>
            <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px 0' }}>
              {messages.map(m => (
                <div key={m.id} style={{ padding: '8px 14px', borderBottom: '1px solid #F3F4F6' }}>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '2px' }}>
                    <strong style={{ color: '#374151' }}>{m.sender_first} {m.sender_last}</strong>
                    {' → '}{m.recipient_first} {m.recipient_last}
                    {'  ·  '}{new Date(m.created_at).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '13px', color: '#111827', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</div>
                  {m.email_ref && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '3px' }}>📎 Email: {m.email_ref.subject || '(no subject)'}</div>}
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}
